
import { Policy } from "../types";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProducerInfo } from "../form/components/ProducerInfo";

interface PolicyDetailsProps {
  policy: Policy;
  producerName: string;
  onProgressiveClick?: () => void;
}

export const PolicyDetails = ({ policy, producerName, onProgressiveClick }: PolicyDetailsProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Fetch producer data directly from profiles table using producer_id
  const { data: producerData } = useQuery({
    queryKey: ['producer', policy.producer_id],
    queryFn: async () => {
      if (!policy.producer_id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, email')
        .eq('id', policy.producer_id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching producer data:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!policy.producer_id
  });

  // Calculate the original premium (total premium minus endorsement premium)
  const originalPremium = policy.endorsement_premium 
    ? policy.premium_amount - policy.endorsement_premium 
    : policy.premium_amount;

  return (
    <div className="space-y-4 w-full max-w-2xl">
      <div className="flex flex-wrap gap-3 mb-2">
        <Badge
          className={`${getStatusColor(policy.status)} capitalize font-medium`}
        >
          {policy.status}
        </Badge>
        
        {policy.insurance_carrier === 'Progressive' && (
          <Badge 
            className="bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
            onClick={onProgressiveClick}
          >
            Progressive ForAgentsOnly
          </Badge>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {policy.insurance_type}
            {policy.policy_line && ` - ${policy.policy_line}`}
          </h3>
          
          <div className="space-y-1">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Policy Number:</span> {policy.policy_number}
            </p>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Producer:</span> {' '}
              {producerData ? (
                <span>{producerData.first_name} {producerData.last_name}</span>
              ) : (
                <span>Not Assigned</span>
              )}
            </div>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Premium:</span> {formatCurrency(originalPremium)}
              {policy.endorsement_premium ? (
                <span className="text-emerald-600 ml-1">
                  + {formatCurrency(policy.endorsement_premium)} endorsement
                </span>
              ) : null}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Dates:</span> {formatDate(policy.start_date)} to {formatDate(policy.end_date)}
            </p>
            
            <p className="text-sm text-gray-600">
              <span className="font-medium">Carrier:</span> {policy.insurance_carrier || 'Not specified'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Lead Source:</span> {policy.lead_source || 'Not specified'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Type:</span> {policy.policy_acquisition_type || 'Not specified'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Created:</span> {formatDistanceToNow(new Date(policy.created_at), { addSuffix: true })}
            </p>
            
            {policy.endorsement_date && policy.endorsement_premium && (
              <div className="mt-2 bg-amber-50 p-2 rounded-md">
                <p className="text-sm text-amber-800 font-medium">Endorsement</p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Date:</span> {formatDate(policy.endorsement_date)}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Premium:</span> {formatCurrency(policy.endorsement_premium)}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Total Premium:</span> {formatCurrency(policy.premium_amount)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
